import { FilterCondition } from "@irohana-dev/components";
import { FC, useMemo } from "react";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useFilterConditionPanel } from "@/components/commons/filter-condition/use-filter-condition-panel";
import { TFilterConditionPanelConfig } from "@/components/commons/filter-condition/types";
import { buildJobFilterFields, fromFilterValue, toFilterValue } from "@/features/job/components/job-list/job-filter-condition-adapter";
import {
  TJobDisplayColumnSetting,
  TJobFilterFormExtended,
  defaultJobDisplayColumns,
  defaultJobFilterValues,
  normalizeJobDateFields,
  normalizeJobMultiSelectFields,
  stripEmptyJobFilterFields,
  JOB_LOCKED_COLUMN_IDS,
} from "@/features/job/components/job-list/job-filter-shared";
import { useJobFilterMasterData } from "@/features/job/components/job-list/use-job-filter-master-data";
import { JOB_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/job/constants/common";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { FILTER_CONDITION_PANEL_WIDTH } from "@/components/commons/filter-condition/filter-condition-panel-width";

const panelConfig: TFilterConditionPanelConfig<TJobFilterFormExtended, TJobDisplayColumnSetting> = {
  pageType: "job",
  defaultFilters: defaultJobFilterValues,
  defaultColumns: defaultJobDisplayColumns,
  columnsStorageKey: JOB_DISPLAY_COLUMNS_STORAGE_KEY,
  lockedColumnIds: JOB_LOCKED_COLUMN_IDS,
  normalizeFilters: (filters) => normalizeJobMultiSelectFields(normalizeJobDateFields(filters)),
  toFilterValue,
  fromFilterValue,
  stripEmptyFilters: stripEmptyJobFilterFields,
  getDateRange: (filters) => ({ from: filters.registrationDateFrom, to: filters.registrationDateTo }),
};

type Props = {
  isOpen: boolean;
  mode?: "create" | "edit";
  onClose: () => void;
  onApplyFilters?: (filters: TJobFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TJobDisplayColumnSetting[]) => void;
  initialFilters?: Partial<TJobFilterFormExtended>;
  initialDisplayColumns?: TJobDisplayColumnSetting[];
  appliedConditionId?: number | null;
  appliedConditionName?: string;
  appliedConditionShareScope?: TSavedConditionShareScope;
  appliedConditionIsDefault?: boolean;
  appliedConditionIncludeColumnSettings?: boolean;
  appliedConditionColumnSettings?: { order?: string[]; hidden?: string[] } | null;
  onConditionSaved?: (id: number, title: string) => void;
};

export const JobFilterConditionPanel: FC<Props> = ({ mode = "create", ...rest }) => {
  const { truncatedOptions } = useJobFilterMasterData();
  const panel = useFilterConditionPanel(panelConfig, { ...rest, mode });

  const fields = useMemo(() => buildJobFilterFields(truncatedOptions), [truncatedOptions]);

  return (
    <>
      <FilterCondition
        width={FILTER_CONDITION_PANEL_WIDTH}
        shareColor={filterConditionShareColor}
        isOpen={rest.isOpen}
        mode={panel.displayMode}
        onClose={rest.onClose}
        fields={fields}
        value={panel.value}
        onChange={panel.setValue}
        columns={panel.columnOptions}
        columnOrder={panel.columnOrder}
        hiddenColumns={panel.hiddenColumns}
        lockedColumns={JOB_LOCKED_COLUMN_IDS}
        onColumnSettingsChange={panel.handleColumnSettingsChange}
        conditionName={panel.conditionName}
        onConditionNameChange={panel.handleConditionNameChange}
        conditionNameError={panel.conditionNameError}
        appliedConditionId={rest.appliedConditionId ?? undefined}
        shareScope={panel.shareScope}
        onShareScopeChange={panel.setShareScope}
        isDefault={panel.isDefault}
        onIsDefaultChange={panel.setIsDefault}
        includeColumnSettings={panel.includeColumnSettings}
        onIncludeColumnSettingsChange={panel.setIncludeColumnSettings}
        hasSavedColumnSettings={panel.hasSavedColumnSettings}
        onSaveCondition={panel.handleSaveCondition}
        isSaving={panel.isSaving}
        onApply={panel.handleApply}
        onClear={() => panel.hasDataToClear && panel.setIsClearConfirmOpen(true)}
      />
      <ConfirmDeleteModal
        open={panel.isClearConfirmOpen}
        onClose={() => panel.setIsClearConfirmOpen(false)}
        onOk={panel.clearValue}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
